import { useEffect, useRef } from 'react'

// Test sitekey that always passes — used in dev when VITE_TURNSTILE_SITE_KEY
// is not set. Production key bound to forms.claudemy.org.
const FALLBACK_DEV_KEY = '1x00000000000000000000AA'

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string
          callback?: (token: string) => void
          'expired-callback'?: () => void
          'error-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
        }
      ) => string
      reset: (id: string) => void
      remove: (id: string) => void
    }
  }
}

let scriptPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject()
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile]')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('turnstile script error')))
      return
    }
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    script.dataset.turnstile = '1'
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('turnstile script error'))
    document.head.appendChild(script)
  })
  return scriptPromise
}

interface TurnstileProps {
  onToken: (token: string) => void
  onExpired?: () => void
  onError?: () => void
  className?: string
}

export function Turnstile({ onToken, onExpired, onError, className }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  // Stable refs — RHF re-renders should not re-mount the widget.
  const onTokenRef = useRef(onToken)
  const onExpiredRef = useRef(onExpired)
  const onErrorRef = useRef(onError)
  onTokenRef.current = onToken
  onExpiredRef.current = onExpired
  onErrorRef.current = onError

  useEffect(() => {
    let cancelled = false
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? FALLBACK_DEV_KEY

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onExpiredRef.current?.(),
          'error-callback': () => onErrorRef.current?.(),
          theme: 'auto',
        })
      })
      .catch(() => onErrorRef.current?.())

    return () => {
      cancelled = true
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          /* widget already gone */
        }
        widgetIdRef.current = null
      }
    }
  }, [])

  return <div ref={containerRef} className={className} />
}
